import Cookies from 'js-cookie';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { authApi } from '../api/auth';
import apiClient from '../api/client';
interface AuthContextType {
  user: {
    id: string;
    email: string;
    name: string;
    profile:string;
    role: string;
  } | null;
  setUser:any;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{
    user: {
      id: string;
      email: string;
      name: string;
      role: string;
    };
    token: string;
  }>;
  logout: () => Promise<void>;
  setIsLoading: (value: boolean) => void;
  checkAuth: () => Promise<void>;
  siteData: any;
  fetchSettingsData: ()=> void;

  landingPageData: any | null;
  setLandingPageData: React.Dispatch<React.SetStateAction<any | null>>;
  landingDataLoading: boolean;
  setLandingDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [siteData, setSiteData] = useState<any | null>(() => {
    const cached = localStorage.getItem("site-settings");
    return cached ? JSON.parse(cached) : null;
  });

  const [landingPageData, setLandingPageData] = useState<any | null>(null)
  const [landingDataLoading, setLandingDataLoading] = useState(true)

    const fetchSettingsData=async()=>{
      try{
         const {data} = await apiClient.get(`/admin/get-settings`)

         if (data?.success && data?.settings) {
            setSiteData(data.settings);
            localStorage.setItem("site-settings", JSON.stringify(data.settings));
         }
      }
      catch(error: any){
         toast.error(error?.response?.data?.message || error?.message)
      }
   }

  useEffect(() => {
    checkAuth();
    fetchSettingsData();
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<any> => {
    try {
      setIsLoading(true);
      const response = await authApi.login({ email, password });

      if (!response.success) {
        toast.warning(response.message);
      }

      // Store token in localStorage
      localStorage.setItem('user', JSON.stringify(response.data.user));

      if (rememberMe) {
        const credentials = {
          email: email,
          password: password
        };
        localStorage.setItem('rememberedCredentials', JSON.stringify(credentials));
      } else {
        localStorage.removeItem('rememberedCredentials');
      }

      // Set the user data
      const userData = {
       ...response.data.user,
        // token: response.data.token
      };
      setUser(userData);
      toast.success(response.message);
      return userData;

    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const token = Cookies.get('authToken')
      if (!token) {
        setUser(null);
        return;
      }
      const user = JSON.parse(localStorage.user)

      setUser(user);
    } catch (error) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      const response = await authApi.logout();
      if (response.success) {
        toast.warning(response.message);
      } else {
        toast.success(response.message);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  };

  const value = {
    user,
    setUser,
    isAuthenticated: !!user,
    isLoading,
    setIsLoading,
    login,
    logout,
    checkAuth,
    siteData,
    fetchSettingsData,

    landingPageData,
    setLandingPageData,
    landingDataLoading,
    setLandingDataLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};